// V2 版式：image-text（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/image-text.ts 逐坐标移植
// 左文右图（文案区 : 图区 ≈ 5:7），供料 image block + bullets/text block。
// 有 b.url 时真实图片 cover 裁切；无则占位框（grey1 底 + 虚线边框 + 细线小方块 + 等宽 IMAGE）。
// 图框直角（swiss radius=0）或微圆角（magazine skin.radius）。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { luxeExt } from "./luxe";
import { brutalTheme, termTheme, reportTheme, inkTheme } from "./themes";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

export default function V2ImageText({ c, skin, page, total, editable, handlers, onImageClick }: V2LayoutProps) {
  // 与后端 image-text.ts GEO 同源（deno dump 生成，见 v2geo.generated.ts 头部注释）
  const GEO = V2GEO["image-text"] as any;
  const m = SPACING.marginX;
  const W = 13.333;
  const mag = skin.style === "magazine";
  // 占位框底色：瑞士 grey1 / 杂志 paperTint（后端 image-text.ts:39-41）/ luxe 浅金 blush
  // （v2geo 快照的 luxe theme.paperTint 是 "#undefined" 坏值——luxe 必须先命中 ext.blush）
  const ext = luxeExt(skin);
  const theme = skin.theme as { grey?: { grey1?: string }; paperTint?: string };
  // 占位框底色链（image-text.ts:45-55）：brutal 白报纸/term 近黑/report inkDeep/ink 暖纸
  const brutalT = brutalTheme(skin);
  const termT = termTheme(skin);
  const reportT = reportTheme(skin);
  const inkT = inkTheme(skin);
  const grey1 = theme.grey?.grey1
    ?? (ext ? ext.blush
    : theme.paperTint
    ?? (brutalT ? brutalT.paper : termT ? termT.paper : reportT ? reportT.inkDeep : inkT ? inkT.paper : skin.hairline));

  const tx = m;
  const imgX = tx + GEO.textW + GEO.gap;
  const imgW = W - m - imgX;
  const imgH = GEO.bottomY - GEO.topY;

  const bbi = c.blocks.findIndex((x) => x.type === "bullets");
  const bb = bbi >= 0 ? c.blocks[bbi] : null;
  const tbi = c.blocks.findIndex((x) => x.type === "text");
  const tb = tbi >= 0 ? c.blocks[tbi] : null;
  const ii = c.blocks.findIndex((x) => x.type === "image");
  const img = ii >= 0 ? c.blocks[ii] : null;
  const src = img && img.type === "image" ? img.url : undefined;

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "image-text")} page={page} total={total} foot={footOf()} />

      {/* ---- 左：文案区 ---- */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(tx), top: px(GEO.topY), width: px(GEO.textW), height: px(GEO.titleH),
          fontSize: pt(titleSize(c.title, Math.round(skin.scale.pageTitle * 0.8))), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: mag ? 700 : 400, lineHeight: 1.15,
        }}
      />
      {(c.subtitle || editable) && (
        <EditableText
          value={c.subtitle ?? ""} path="subtitle" editable={editable}
          onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
          style={{
            position: "absolute", left: px(tx), top: px(GEO.subY), width: px(GEO.textW), height: px(GEO.subH),
            fontSize: pt(skin.scale.body), color: skin.sub, fontFamily: skin.fontBody,
          }}
        />
      )}

      {bb && bb.type === "bullets"
        ? bb.items.slice(0, 4).map((t, i) => {
            const y = GEO.bulletsY + i * GEO.bulletGap;
            return (
              <div key={i}>
                {/* accent 小方块（静态装饰） */}
                <div style={{
                  position: "absolute", left: px(tx), top: px(y + 0.08),
                  width: px(GEO.markerSize), height: px(GEO.markerSize), background: skin.accent,
                }} />
                <EditableText
                  value={t} path={`blocks.${bbi}.items.${i}`} editable={editable} multiline
                  onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                  style={{
                    position: "absolute", left: px(tx + GEO.markerPad), top: px(y),
                    width: px(GEO.textW - GEO.markerPad), height: px(GEO.bulletGap - 0.1),
                    fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody, lineHeight: 1.25,
                  }}
                />
              </div>
            );
          })
        : tb && tb.type === "text" && (
          <EditableText
            value={tb.text} path={`blocks.${tbi}.text`} editable={editable} multiline
            onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
            style={{
              position: "absolute", left: px(tx), top: px(GEO.bulletsY),
              width: px(GEO.textW), height: px(GEO.bottomY - GEO.bulletsY),
              fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody, lineHeight: 1.4,
            }}
          />
        )}

      {/* ---- 右：图区 ---- */}
      <div
        onClick={() => { if (ii >= 0) onImageClick?.(ii); }}
        style={{
          position: "absolute", left: px(imgX), top: px(GEO.topY), width: px(imgW), height: px(imgH),
          cursor: editable && ii >= 0 ? "pointer" : undefined,
        }}
      >
        {src ? (
          <img
            src={src} alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <>
            {/* 占位框：grey1 底 + 虚线边框（直角或 skin.radius 微圆角）；brutal 粗黑实线框/term/report/ink 细实线框（image-text.ts:109-124） */}
            <div style={{
              position: "absolute", inset: 0,
              background: grey1,
              border: brutalT ? `3px solid ${brutalT.ink}`
                : termT ? `1px solid ${termT.muted}`
                : reportT ? `0.7px solid ${reportT.muted}`
                : inkT ? `1px solid ${inkT.accentGrey}`
                : `1px dashed ${skin.hairline}`,
              borderRadius: skin.radius > 0 ? px(skin.radius) : 0,
            }} />
            {/* 占位标识：细线小方块 + 等宽 IMAGE（不用 emoji） */}
            <div style={{
              position: "absolute", left: px(imgW / 2 - 0.25), top: px(imgH / 2 - 0.85),
              width: px(0.5), height: px(0.5), border: `1px solid ${skin.sub}`,
            }} />
            <div style={{
              position: "absolute", left: 0, top: px(imgH / 2 - 0.25), width: "100%", height: px(0.3),
              fontSize: pt(skin.scale.meta), color: skin.sub, fontFamily: skin.fontMono,
              textAlign: "center", letterSpacing: "0.3em",
            }}>IMAGE</div>
            <div style={{
              position: "absolute", left: px(0.5), top: px(imgH / 2 + 0.05), width: px(imgW - 1), height: px(1.0),
              fontSize: pt(skin.scale.caption), color: skin.sub, fontFamily: skin.fontBody,
              textAlign: "center", lineHeight: 1.3,
            }}>{img && img.type === "image" && img.prompt ? img.prompt : "图片占位"}</div>
          </>
        )}
      </div>
    </>
  );
}
